import React, { useState } from 'react';
import { InvoicesTabIcon, RedCloseIcon, ShareIcon, VerticalListActionsIcon } from 'assets/icons';
import { CustomPopover } from 'components';
import { getInitials } from 'utils/commonUtils';
import CustomAvatar from 'components/avatar/CustomAvatar';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { getInvoiceStatus, getInvoiceStatusColor } from 'containers/invoices/utils';
import { InvoiceDetails } from 'containers/estimates/estimates.model';
import { InvoiceStatus } from '../invoices.model';
import { PaymentInfo, RelatedInvoiceForJob } from 'containers/jobs/jobs.model';
import { If } from 'components';
import { getPaymentMethod } from 'containers/receipts/util';

interface RelatedInvoiceCardProps {
  invoice: InvoiceDetails | RelatedInvoiceForJob;
  onInvoiceClick: (id: string) => void;
  onRemoveInvoice?: (id: string) => void;
  onShareInvoice?: (invoice: InvoiceDetails | RelatedInvoiceForJob) => void;
  className?: string;
  fromBillingTab?: boolean;
  showPaymentDetails?: boolean;
  paymentInfo?: PaymentInfo;
}

const RelatedInvoiceCard: React.FC<RelatedInvoiceCardProps> = ({
  invoice,
  onInvoiceClick,
  onRemoveInvoice,
  onShareInvoice,
  className = '',
  fromBillingTab = false,
  showPaymentDetails = false,
  paymentInfo
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const togglePopover = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };

  const isPaid = invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.PAID_QR;

  return (
    <div
      className={`${className} rounded border border-borderGray  
        ${invoice.hasPermission ? 'cursor-pointer' : 'pointer-events-none'}`}
      onClick={e => {
        e.stopPropagation();
        if (invoice.hasPermission) onInvoiceClick(invoice.id);
      }}>
      <div className="flex items-center justify-between py-3 px-4">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center 
          justify-center rounded-[10px] bg-secondaryBtn text-center">
            <InvoicesTabIcon className="shrink-0" />
          </div>

          <div>
            <div className="text-sbase text-tertiaryText">{invoice.invoiceNumber}</div>
            <div className="flex items-center gap-2">
              <div className="text-px13 text-accentText">
                {DateUtils.getDateInFormat({
                  date: new Date(invoice.createdAt),
                  dateFormat: 'dd MMM yyyy'
                })}
              </div>
              <div className="h-1 w-1 rounded-full bg-accentText"></div>
              <div>
                <CustomAvatar
                  src={invoice.createdBy['profilePicture'] ?? invoice.createdBy['profilePictureURL']}
                  width="w-4"
                  height="h-4"
                  fontStyle="text-[8px]">
                  {getInitials(invoice.createdBy.name)}
                </CustomAvatar>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className={'text-17px font-bold text-primaryText'}>{getAmountWithCurrency(invoice.amountPayable)}</div>

            <div
              className={'text-right text-px13'}
              style={{ color: getInvoiceStatusColor(invoice.status as InvoiceStatus) }}>
              {getInvoiceStatus(invoice.status as InvoiceStatus)}
            </div>
          </div>
          <If condition={invoice.hasPermission && !fromBillingTab}>
            <CustomPopover
              anchorComponentId={`invoice-${invoice.id}`}
              anchorComponent={() => (
                <VerticalListActionsIcon
                  height={20}
                  width={4}
                  className={'mx-2 flex shrink-0 cursor-pointer'}
                  id="invoice-option-click"
                />
              )}
              show={isPopoverOpen}
              onTogglePopover={togglePopover}
              onClosePopover={handleClosePopover}
              offset="-right-1"
              minWidth="min-w-[180px]"
              showArrow={true}
              highlightAnchor={true}
              padding="p-3">
              <div
                className="flex cursor-pointer items-center gap-2.5 pb-2 hover:bg-highlightGray"
                role="presentation"
                onClick={e => {
                  e.stopPropagation();
                  onShareInvoice(invoice);
                  handleClosePopover();
                }}>
                <ShareIcon className="w-4" />
                <div className="text-sbase font-semibold text-primaryText">Share invoice</div>
              </div>
              <div
                className="flex cursor-pointer items-center gap-2.5 hover:bg-highlightGray"
                role="presentation"
                onClick={e => {
                  e.stopPropagation();
                  onRemoveInvoice(invoice.id);
                  handleClosePopover();
                }}>
                <RedCloseIcon className="w-4" />
                <div className="text-sbase font-semibold text-primaryText">Remove invoice</div>
              </div>
            </CustomPopover>
          </If>
        </div>
      </div>
      {showPaymentDetails && paymentInfo && isPaid && (
        <div className="border-t border-borderGray">
          <div className="flex items-center gap-1 px-3 py-1 text-px13 text-accentText">
            Payment done via{' '}
            {getPaymentMethod({
              cardLast4: paymentInfo?.cardLast4,
              network: paymentInfo?.network,
              paymentMethod: paymentInfo?.paymentMethod,
              isManualKeyed: false
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedInvoiceCard;
